/**
 * Sentry SDK initialization for the React frontend.
 *
 * - Complete no-op when REACT_APP_SENTRY_DSN is unset.
 * - No session replay. No PII.
 * - Strips known auth tokens from URL query strings before transmission.
 *
 * Env vars (set in /app/frontend/.env):
 *   REACT_APP_SENTRY_DSN               — activation switch. Blank = disabled.
 *   REACT_APP_SENTRY_ENVIRONMENT       — e.g. production, staging.
 *   REACT_APP_SENTRY_TRACES_SAMPLE_RATE — float 0..1, default 0.05.
 */
import * as Sentry from "@sentry/react";

const DSN = (process.env.REACT_APP_SENTRY_DSN || "").trim();

const SENSITIVE_PARAMS = ["token", "auth", "session", "api_key", "access_token"];

function sanitizeUrl(raw) {
  try {
    const url = new URL(raw, window.location.origin);
    let changed = false;
    for (const key of SENSITIVE_PARAMS) {
      if (url.searchParams.has(key)) {
        url.searchParams.set(key, "[redacted]");
        changed = true;
      }
    }
    return changed ? url.toString() : raw;
  } catch {
    return raw;
  }
}

function beforeSend(event) {
  // Drop events from `file://` origins. These come from users who saved
  // the page locally and opened the HTML file off disk — `BrowserRouter`
  // can't manipulate the History API in a file-protocol document and
  // throws a SecurityError on first mount. Not a real bug we can fix,
  // and not actionable on our side. (Issue 7523145530, 6/2/2026.)
  if (typeof window !== "undefined" && window.location?.protocol === "file:") {
    return null;
  }
  // Belt-and-suspenders: scan the event's request URL too, in case the
  // event was queued before location was set (rare race in SDK boot).
  const reqUrl = event?.request?.url || "";
  if (reqUrl.startsWith("file:") || reqUrl.startsWith("file://")) {
    return null;
  }
  // Drop errors thrown by mobile in-app webview native bridges — these
  // are from Instagram/Facebook/TikTok/etc. trying to talk to their iOS
  // or Android native shell, not from our React code. The patterns:
  //   iOS:     "undefined is not an object (evaluating 'window.webkit.messageHandlers...')"
  //   Android: "Error invoking postMessage: Java object is gone"
  //            (from `iabjs://navigation_performance_logger_android` — IG's
  //             in-app browser performance tracker)
  // They look like our errors because Sentry's global onerror handler
  // captures anything thrown in the page, including the in-app
  // browser shim. (Issues 7532510024 + 7534471087, 6/6+7/2026.)
  const errMsg = (
    event?.exception?.values?.[0]?.value
    || event?.message
    || ""
  );
  if (typeof errMsg === "string") {
    const inAppBridgeRe = /(window\.webkit\.messageHandlers|window\.AndroidBridge|FBSDKMessenger|__fbBatchedBridge|iabjs:\/\/|ig:\/\/|fb:\/\/|Java object is gone)/;
    if (inAppBridgeRe.test(errMsg)) {
      return null;
    }
  }
  // Also scan the stacktrace — Instagram's Android performance logger
  // throws errors whose `value` is generic ("Error invoking postMessage:
  // Java object is gone") but whose stack frames reference the
  // `iabjs://` URL scheme. If any frame's filename matches an in-app
  // browser pseudo-URL, drop the event.
  const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];
  for (const f of frames) {
    const fn = f?.filename || "";
    if (/^(iabjs|ig|fb):\/\//.test(fn)) {
      return null;
    }
  }
  if (event?.request?.url) {
    event.request.url = sanitizeUrl(event.request.url);
  }
  if (event?.request) {
    delete event.request.cookies;
    delete event.request.data;
  }
  if (event?.user) {
    delete event.user.email;
    delete event.user.ip_address;
    delete event.user.username;
  }
  return event;
}

let initialized = false;

export function initSentry() {
  if (initialized || !DSN) return false;
  try {
    Sentry.init({
      dsn: DSN,
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "development",
      tracesSampleRate: parseFloat(
        process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0.05",
      ),
      sendDefaultPii: false,
      integrations: [
        Sentry.browserTracingIntegration(),
      ],
      beforeSend,
      beforeSendTransaction: (event) => {
        // Skip /api/health probes.
        const name = event?.transaction || "";
        if (name.includes("/api/health")) return null;
        return event;
      },
    });
    initialized = true;
    return true;
  } catch (e) {
    // Never let Sentry init break the app.
    // eslint-disable-next-line no-console
    console.warn("Sentry init failed:", e);
    return false;
  }
}

export function setSentryUser(userId, email) {
  if (!initialized) return;
  try {
    // Hash email client-side so we never send plain PII.
    const emailHash = email
      ? btoa(unescape(encodeURIComponent(email.toLowerCase().trim()))).slice(0, 12)
      : undefined;
    Sentry.setUser({ id: String(userId), ...(emailHash ? { email_hash: emailHash } : {}) });
  } catch {
    // Observability is best-effort — never let Sentry tagging crash auth.
  }
}

export function clearSentryUser() {
  if (!initialized) return;
  try {
    Sentry.setUser(null);
  } catch {
    // Observability is best-effort — never let Sentry tagging crash logout.
  }
}

// Initialize at module load so this runs before any React code.
initSentry();
